import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Motorcycle,
  MagnifyingGlass,
  Plus,
  Funnel,
  CheckCircle,
  Clock,
  Wrench,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function MotorcycleListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [motorcycles, setMotorcycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(searchParams.get("status") || "");

  useEffect(() => {
    fetchMotorcycles();
  }, [filter]);

  const fetchMotorcycles = async () => {
    setLoading(true);
    try {
      let url = `${API}/motorcycles`;
      const params = new URLSearchParams();
      if (filter) params.append("status", filter);
      if (search) params.append("search", search);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { withCredentials: true });
      setMotorcycles(response.data);
    } catch (error) {
      console.error("Error fetching motorcycles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMotorcycles();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return (
          <CheckCircle
            size={18}
            weight="fill"
            className="text-[#00E676] shrink-0"
          />
        );
      case "assembly":
        return <Wrench size={18} className="text-yellow-500 shrink-0" />;
      default:
        return <Clock size={18} className="text-[#A1A1AA] shrink-0" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Finalizada";
      case "assembly":
        return "Em Montagem";
      default:
        return "Pendente";
    }
  };

  const filterOptions = [
    { value: "", label: "Todas" },
    { value: "pending", label: "Pendentes" },
    { value: "assembly", label: "Em Montagem" },
    { value: "completed", label: "Finalizadas" },
  ];

  return (
    <Layout title="Motos">
      <div className="space-y-4 pb-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 w-full">
          <div className="relative flex-1 w-full min-w-0">
            <MagnifyingGlass
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field !pl-12 h-12 w-full"
              placeholder="Buscar chassi ou modelo..."
              data-testid="search-input"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-4 bg-[#1A1A1A] rounded-lg border border-white/10 flex items-center justify-center shrink-0 hover:bg-[#222] transition-colors"
            data-testid="search-button"
          >
            <MagnifyingGlass size={20} />
          </button>
        </form>

        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Funnel size={18} className="text-[#A1A1AA] flex-shrink-0" />
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setFilter(option.value);
                if (option.value) {
                  setSearchParams({ status: option.value });
                } else {
                  setSearchParams({});
                }
              }}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                filter === option.value
                  ? "bg-[#00E676] text-black font-medium"
                  : "bg-[#1A1A1A] text-white border border-white/10"
              }`}
              data-testid={`filter-${option.value || "all"}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
          </div>
        ) : motorcycles.length === 0 ? (
          <div className="card p-8 text-center">
            <Motorcycle size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="text-[#A1A1AA] mb-4">Nenhuma moto encontrada</p>
            <Link
              to="/nova-moto"
              className="btn-primary inline-flex items-center justify-center gap-2 w-auto px-6"
              data-testid="empty-new-moto"
            >
              <Plus size={20} weight="bold" />
              Nova Moto
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {motorcycles.map((moto) => (
              <Link
                key={moto.id}
                to={`/motos/${moto.id}`}
                className="card p-3 sm:p-4 flex items-center gap-3 sm:gap-4 hover:bg-[#1A1A1A] transition-colors border border-transparent hover:border-white/5"
                data-testid={`moto-item-${moto.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                  <Motorcycle size={28} className="text-[#A1A1AA]" />
                </div>
                <div className="flex-1 min-w-0 pr-2">
                  <p className="font-mono text-sm sm:text-base font-bold truncate">
                    {moto.chassis_sn}
                  </p>
                  <p className="text-xs sm:text-sm text-[#A1A1AA] truncate mt-0.5">
                    {moto.model}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs bg-[#1A1A1A] px-2 py-0.5 rounded font-medium border border-white/5">
                      {moto.voltage}V
                    </span>
                    <span className="text-xs text-[#A1A1AA]">
                      {moto.batteries?.length || 0}/
                      {moto.voltage === 60 ? 5 : 6} bat.
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 bg-[#121212] sm:bg-transparent px-2.5 py-1.5 sm:px-0 sm:py-0 rounded-lg">
                  {getStatusIcon(moto.status)}
                  <span className="text-xs text-[#A1A1AA] hidden sm:block font-medium">
                    {getStatusText(moto.status)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* FAB */}
        <Link
          to="/nova-moto"
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#00E676] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.3)] hover:bg-[#00C853] hover:scale-105 transition-all z-20"
          data-testid="fab-new-moto"
        >
          <Plus size={28} weight="bold" className="text-black" />
        </Link>
      </div>
    </Layout>
  );
}
